import { Button, Field } from "../components/rumonai";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";
import { ApiError } from "../lib/api";
import { APPEARANCE_KEY, applyAppearance, readAppearance } from "../lib/appearance";
import { AdminPages } from "./AdminPages";
import { platformPost, platformRequest, type PlatformAccount } from "./api";
import { PlatformError, PlatformLoading } from "./shared";
import "./platform.css";
import "./admin.css";

type AdminSession = { account: PlatformAccount | null; initialized: boolean };

export default function AdminApp() {
  const [path, setPath] = useState(() => location.hash || "#/usage");
  const [session, setSession] = useState<AdminSession | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  const [loggingOut, setLoggingOut] = useState(false);
  const leaving = useRef(false);
  // The console follows the browser's one appearance choice (light, dark, palette), like the owner pages.
  useLayoutEffect(() => {
    applyAppearance(readAppearance());
    const changed = (event: StorageEvent) => {
      if (event.key === null || event.key === APPEARANCE_KEY) applyAppearance(readAppearance());
    };
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    const system = () => applyAppearance(readAppearance());
    window.addEventListener("storage", changed);
    media?.addEventListener?.("change", system);
    return () => { window.removeEventListener("storage", changed); media?.removeEventListener?.("change", system); };
  }, []);
  useEffect(() => {
    const changed = () => setPath(location.hash || "#/usage");
    const visible = () => { if (!document.hidden) reload(); };
    const expired = () => setSession(current => current ? { ...current, account: null } : null);
    window.addEventListener("hashchange", changed);
    window.addEventListener("pa:admin:expired", expired);
    document.addEventListener("visibilitychange", visible);
    document.title = "涟漪 · 管理后台";
    return () => {
      window.removeEventListener("hashchange", changed);
      window.removeEventListener("pa:admin:expired", expired);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [reload]);
  useEffect(() => {
    const controller = new AbortController();
    void platformRequest<AdminSession>("/api/admin/session", { signal: controller.signal })
      .then(value => { if (!controller.signal.aborted) { setSession(value); setError(null); } })
      .catch(failure => { if (!controller.signal.aborted) setError(failure); });
    return () => controller.abort();
  }, [revision, path]);
  const logout = async () => {
    if (leaving.current) return;
    leaving.current = true;
    setLoggingOut(true);
    try {
      await platformPost("/api/admin/logout", {});
      setSession({ account: null, initialized: true });
      setError(null);
    } catch (failure) { setError(failure); }
    finally { leaving.current = false; setLoggingOut(false); }
  };
  return <div className="admin-app">
    <header className="admin-top">
      <a className="admin-brand" href="/admin/">
        <Icon name="shield" size={24} /><strong>涟漪</strong><span>管理后台</span>
      </a>
      <div className="admin-account">
        <a href="/" target="_blank" rel="noopener noreferrer">打开用户端<Icon name="arrow" size={15} /></a>
        {session?.account ? <><span className="admin-account-name">{session.account.name}</span>
          <Button variant="ghost" disabled={loggingOut} aria-busy={loggingOut || undefined}
            onClick={() => { void logout(); }}>{loggingOut ? "正在退出…" : "退出"}</Button></> : null}
      </div>
    </header>
    <main className="admin-main">
      {error ? <PlatformError error={error} retry={reload} /> : null}
      {!session && !error ? <PlatformLoading label="正在确认管理账号" /> : null}
      {session?.account ? <AdminPages path={path} /> : session ?
        <AdminLogin initialized={session.initialized} reset={path === "#/password"} onAccount={account => {
          setSession({ account, initialized: true }); location.hash = "#/usage";
        }} /> : null}
    </main>
  </div>;
}

function AdminLogin({ initialized, reset, onAccount }: {
  initialized: boolean; reset: boolean; onAccount: (account: PlatformAccount) => void;
}) {
  const [verified, setVerified] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const pressed = useRef(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    setVerified(false); setPassword(""); setCode(""); setError(null);
  }, [reset]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pressed.current) return;
    pressed.current = true;
    setBusy(true); setError(null);
    try {
      if (reset && !verified) {
        const result = await platformPost<{ name: string }>("/api/admin/password/check-code",
          { name: name.trim(), code: code.trim() });
        setName(result.name); setVerified(true);
        return;
      }
      const result = await platformPost<{ account: PlatformAccount }>(
        reset ? "/api/admin/password/reset" : "/api/admin/login",
        { name: name.trim(), password, ...(reset ? { code: code.trim() } : {}) },
      );
      setPassword(""); setCode(""); onAccount(result.account);
    } catch (failure) {
      // Never echo supplied passwords or setup codes in the error view.
      setPassword("");
      const status = failure instanceof ApiError ? failure.status : 400;
      if (reset && status === 401) setVerified(false);
      setError(failure instanceof ApiError && failure.status === 0 ? failure : new ApiError(
        reset ? verified ? "未能设置密码" : "设置码验证失败" : "未能登录",
        status === 429 ? "尝试次数较多，请稍后再试。"
          : status === 401 ? reset ? "管理员账号或设置码不正确，或设置码已过期。" : "管理员账号或密码不正确。"
          : status >= 500 ? "服务暂不可用，请稍后重试。"
          : reset ? "请确认新密码至少有 8 个字符。" : "请检查管理员账号和密码。",
        status === 401 && reset ? "请核对账号，并使用最新签发的一次性设置码。" : null,
        status,
      ));
    } finally { pressed.current = false; setBusy(false); }
  };
  const switchMode = () => { location.hash = reset ? "#/usage" : "#/password"; };
  return <section className="admin-login">
    <p className="admin-eyebrow">平台运营</p>
    <h1>{reset ? verified ? "设置管理员密码" : "验证管理员身份" : "登录管理后台"}</h1>
    <p className="admin-login-note">{reset ? verified ? `账号 ${name} · 设置码已验证` : "先验证设置码，再设置登录密码。" : "管理平台服务、账号额度和平台运行。"}</p>
    {!initialized ? <aside className="admin-initialize"><p>管理员账号由平台运维创建。</p></aside> : null}
    <form onSubmit={submit} aria-busy={busy}>
      {!verified ? <Field className="field" label="管理员账号"><input className="text-input" name="username" autoComplete="username" required disabled={busy}
          value={name} onChange={event => { setName(event.target.value); setError(null); }} /></Field> : null}
      {reset && !verified ? <Field className="field" label="一次性设置码"><input className="text-input" autoComplete="one-time-code" spellCheck={false} required disabled={busy}
          value={code} onChange={event => { setCode(event.target.value); setError(null); }} /></Field> : null}
      {!reset || verified ? <Field className="field" label={reset ? "新密码" : "密码"} hint={reset ? "至少 8 个字符。" : undefined}><input className="text-input" type="password" name="password" minLength={reset ? 8 : undefined}
          autoComplete={reset ? "new-password" : "current-password"} required disabled={busy} autoFocus={verified}
          value={password} onChange={event => { setPassword(event.target.value); setError(null); }} /></Field> : null}
      {error ? <PlatformError error={error} /> : null}
      <Button variant="primary" type="submit" aria-busy={busy || undefined}
        disabled={busy || !name.trim() || (reset && !verified ? !code.trim() : !password)}>
        <span className="admin-submit-label">{busy ? <LightMotion state="working" compact /> : null}
          {busy ? "正在提交…" : reset ? verified ? "设置密码并进入" : "验证设置码" : "登录"}</span><Icon name="arrow" size={18} />
      </Button>
    </form>
    <Button variant="ghost" type="button" onClick={switchMode} disabled={busy}>
      {reset ? "返回登录" : "设置密码 / 忘记密码"}
    </Button>
    {reset && !verified ? <p className="admin-help">设置码由平台运维签发。</p> : null}
  </section>;
}
